import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import FAQCard from '../components/FAQCard';
import FAQModal from '../components/FAQModal';
import ConfirmDialog from '../components/ConfirmDialog';

const HomeCompositeLoanFAQPage = () => {
    const [faqsList, setFaqsList] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Frequently Asked Questions',
        section_subtitle: 'Find answers to common questions about Composite Loan'
    });
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingFAQ, setEditingFAQ] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, faqId: null });

    const LOAN_TYPE = 'composite_loan';

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            const [faqsRes, settingsRes] = await Promise.all([
                axios.get(`/api/admin/faqs/${LOAN_TYPE}`),
                axios.get(`/api/admin/faqs/settings/${LOAN_TYPE}`)
            ]);

            setFaqsList(faqsRes.data.data || []);
            if (settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load FAQs data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            if (editingFAQ) {
                await axios.put(`/api/admin/faqs/${editingFAQ.id}`, formData);
                toast.success('FAQ updated successfully');
            } else {
                await axios.post('/api/admin/faqs', formData);
                toast.success('FAQ created successfully');
            }
            setIsModalOpen(false);
            setEditingFAQ(null);
            fetchData();
        } catch (error) {
            console.error('Error saving FAQ:', error);
            toast.error('Failed to save FAQ');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, faqId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/faqs/${deleteConfirm.faqId}`);
            toast.success('FAQ deleted successfully');
            setDeleteConfirm({ isOpen: false, faqId: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting FAQ:', error);
            toast.error('Failed to delete FAQ');
        }
    };

    // Handle settings update
    const handleSettingsSave = async () => {
        try {
            setSavingSettings(true);
            await axios.put(`/api/admin/faqs/settings/${LOAN_TYPE}`, settings);
            toast.success('Settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to update settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">FAQs</h1>
                    <p className="text-slate-500 mt-1">Manage frequently asked questions for Composite Loan</p>
                </div>
                <button
                    onClick={() => {
                        setEditingFAQ(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New FAQ
                </button>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-4 text-center">Section Header Settings</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Title
                        </label>
                        <input
                            type="text"
                            value={settings.section_title}
                            onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Frequently Asked Questions"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">
                            Section Subtitle
                        </label>
                        <input
                            type="text"
                            value={settings.section_subtitle}
                            onChange={(e) => setSettings({ ...settings, section_subtitle: e.target.value })}
                            className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            placeholder="e.g., Find answers to common questions..."
                        />
                    </div>
                </div>
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={handleSettingsSave}
                        disabled={savingSettings}
                        className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50 shadow-md"
                    >
                        {savingSettings ? 'Saving...' : 'Save Settings'}
                    </button>
                </div>
            </div>

            {/* FAQs Grid */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-6">
                <h2 className="text-lg font-bold text-slate-800 mb-6">FAQ Items</h2>
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[1, 2, 3].map((i) => (
                            <div key={i} className="bg-white rounded-2xl h-40 shadow-sm animate-pulse border border-slate-100"></div>
                        ))}
                    </div>
                ) : faqsList.length === 0 ? (
                    <div className="bg-slate-50 rounded-2xl p-12 text-center">
                        <div className="max-w-md mx-auto">
                            <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <QuestionMarkCircleIcon className="h-8 w-8 text-slate-400" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-800 mb-2">No FAQs yet</h3>
                            <p className="text-slate-500 mb-6">Add your first FAQ to help customers.</p>
                            <button
                                onClick={() => {
                                    setEditingFAQ(null);
                                    setIsModalOpen(true);
                                }}
                                className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                            >
                                Add First FAQ
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {faqsList.map((faq) => (
                            <FAQCard
                                key={faq.id}
                                faq={faq}
                                onEdit={(f) => {
                                    setEditingFAQ(f);
                                    setIsModalOpen(true);
                                }}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Modal */}
            <FAQModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingFAQ(null);
                }}
                onSubmit={handleSubmit}
                faq={editingFAQ}
                loading={submitting}
                loanType={LOAN_TYPE}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, faqId: null })}
                onConfirm={confirmDelete}
                title="Delete FAQ"
                message="Are you sure you want to delete this FAQ? This action cannot be undone."
            />
        </div>
    );
};

export default HomeCompositeLoanFAQPage;
